// Phase 90.C.unpaid-nudge (2026-02) — Sticky top banner cảnh báo VĐV có
// registration `payment_status=unpaid` cho giải chưa kết thúc quá 3 ngày.
//
// Đặc điểm (user spec):
//  • Điều kiện `today ≤ tournament.end_date + 3 days` (bao gồm chưa bắt đầu
//    + đang diễn ra + trong 3 ngày sau kết thúc) — user choice 1c.
//  • Nhiều giải cùng lúc → carousel xoay 5s, có tên giải cụ thể — user choice 2b.
//  • KHÔNG có nút X dismiss (banner luôn hiện đến khi trả xong) — user choice 3a.
//  • Click "Thanh toán ngay" → điều hướng `/athlete/personal#my-tournaments`
//    (scroll đến section "Giải của tôi").
//  • Chỉ show khi user login là athlete (có `/athlete/registrations` trả data).
//
// v17.perf.auth-storm (02/2026) — HAR PROD cho thấy banner này gây 9x
// /athlete/registrations do useEffect deps [user?.id, location.pathname]
// re-fetch mỗi route change, bypass React Query. Migrate sang useQuery với
// queryKey ["athlete","registrations"] để share cache với useMyRegistrations.
import { useEffect, useState, useMemo } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { Warning, ArrowRight, ArrowsClockwise } from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { athleteApi } from "../../lib/athleteApi";
import { useAuth } from "../../contexts/AuthContext";


function _endOfGraceMs(reg) {
  // Try tournament.end_date first, else start_date + 1 day.
  const raw = reg.tournament_end_date || reg.end_date || reg.tournament_start_date;
  if (!raw) return null;
  const t = new Date(raw);
  if (Number.isNaN(t.getTime())) return null;
  // +3 days grace period (user spec).
  return t.getTime() + 3 * 24 * 60 * 60 * 1000;
}


export default function UnpaidRegistrationBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [idx, setIdx] = useState(0);

  // v17.perf.auth-storm — Share cache với useMyRegistrations qua queryKey
  // ["athlete","registrations"]. Sau khi user thanh toán, invalidate query
  // (hook payment success handler) sẽ refresh cache TỰ ĐỘNG cho MỌI consumer,
  // banner cũng update mà không phải re-fetch mỗi route change.
  const { data: allRegs, isFetched } = useQuery({
    queryKey: ["athlete", "registrations"],
    enabled: !!user?.id,
    queryFn: async () => {
      try {
        return await athleteApi.registrations();
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const regs = useMemo(() => {
    if (!Array.isArray(allRegs)) return [];
    const now = Date.now();
    return allRegs.filter((it) => {
      const st = (it.payment_status || "").toLowerCase();
      if (st === "paid" || st === "completed") return false;
      if ((it.status || "").toLowerCase() === "cancelled") return false;
      const amt = Number(it.amount_vnd || 0);
      if (amt <= 0) return false;
      const grace = _endOfGraceMs(it);
      if (!grace) return true;
      return now <= grace;
    });
  }, [allRegs]);

  // Reset carousel index khi danh sách thay đổi.
  useEffect(() => { setIdx(0); }, [regs.length]);

  useEffect(() => {
    if (regs.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % regs.length), 5000);
    return () => clearInterval(t);
  }, [regs.length]);

  if (!isFetched && !user?.id) return null;
  if (regs.length === 0) return null;
  // Không show banner trên các trang admin/referee/organizer workspace để tránh nhiễu.
  const path = location.pathname || "";
  if (path.startsWith("/admin") || path.startsWith("/referee") || path.startsWith("/organizer") || path.startsWith("/register/")) {
    return null;
  }

  const cur = regs[idx] || regs[0];
  const amt = Number(cur.amount_vnd || 0).toLocaleString("vi-VN");
  const tName = cur.tournament_name || "Giải";
  const evName = cur.event_name ? ` · ${cur.event_name}` : "";

  const onCta = () => {
    // Phase 90.C.exit-fix-v2 — Route `/athlete/personal` không còn tồn tại
    // sau Phase 85.A.2 (đã gộp vào `/u/:identifier`). Navigate về hồ sơ VĐV
    // hiện tại kèm hash để scroll thẳng tới section "Giải của tôi".
    const identifier = user?.username || user?.id;
    if (identifier) {
      navigate(`/u/${identifier}#my-tournaments`);
      setTimeout(() => {
        const el =
          document.getElementById("my-tournaments") ||
          document.getElementById("my-tournaments-anchor");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } else {
      navigate(`/athlete/tournaments`);
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={cur.id || idx}
        className="sticky top-0 z-[145] w-full bg-gradient-to-r from-[#FF3B30] via-[#FF6A00] to-[#FF8A00] text-white shadow-[0_4px_18px_rgba(255,59,48,0.35)]"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        transition={{ duration: 0.25 }}
        data-testid="unpaid-registration-banner"
      >
        <div className="max-w-6xl mx-auto px-3 py-1.5 flex items-center gap-2">
          <Warning size={16} weight="fill" className="text-white shrink-0 animate-pulse" />
          <div className="flex-1 min-w-0 text-[11.5px] font-bold leading-tight overflow-hidden">
            {/* Phase 90.C.exit-fix-v2 — Nội dung 2 dòng trên mobile để tránh
                bị nút "Thanh toán ngay" đè lên; single-line trên desktop. */}
            <div className="flex items-center gap-1 whitespace-nowrap">
              <span className="font-black">Chưa hoàn tất lệ phí</span>
              {regs.length > 1 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-white/85 bg-black/25 px-1.5 rounded-full">
                  <ArrowsClockwise size={9} weight="bold" className="animate-spin" style={{ animationDuration: "3s" }} />
                  {idx + 1}/{regs.length}
                </span>
              )}
            </div>
            <div className="truncate">
              <span className="truncate">{tName}{evName}</span>
              <span className="mx-1 text-white/80">·</span>
              <span className="font-black tabular-nums">{amt}đ</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onCta}
            className="shrink-0 px-2.5 py-1 rounded-lg bg-white/95 hover:bg-white text-[#B00020] text-[10.5px] font-black tracking-wider uppercase flex items-center gap-1 transition"
            data-testid="unpaid-banner-cta"
          >
            <span className="hidden sm:inline">Thanh toán ngay</span>
            <span className="sm:hidden">Trả</span>
            <ArrowRight size={11} weight="bold" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
